import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Spinner } from 'react-bootstrap';

const normalizedId = value =>
  value === null || value === undefined || value === ''
    ? null
    : String(typeof value === 'object' ? value.id : value);

const sortByPositionAndName = (left, right) => {
  const positionDifference =
    Number(left?.position || 0) - Number(right?.position || 0);
  if (positionDifference) return positionDifference;
  return String(left?.name || '').localeCompare(String(right?.name || ''));
};

export const buildDetailViewDestinations = (categories, sourceCategoryId) => {
  const categoryList = Array.isArray(categories) ? categories : [];
  const sourceId = normalizedId(sourceCategoryId);
  const categoriesById = new Map(
    categoryList
      .filter(category => normalizedId(category?.id))
      .map(category => [normalizedId(category.id), category])
  );
  const childrenByParentId = new Map();

  categoryList.forEach(category => {
    const categoryId = normalizedId(category?.id);
    if (!categoryId) return;
    const parentId = normalizedId(category?.parent);
    const key = parentId && categoriesById.has(parentId) ? parentId : 'root';
    const children = childrenByParentId.get(key) || [];
    children.push(category);
    childrenByParentId.set(key, children);
  });

  const destinations = [];
  const visited = new Set();
  const visit = (category, path) => {
    const categoryId = normalizedId(category?.id);
    if (!categoryId || visited.has(categoryId)) return;
    visited.add(categoryId);

    const name = String(category?.name || 'Untitled Category').trim();
    const categoryPath = [...path, name];
    if (categoryId !== sourceId) {
      destinations.push({
        id: categoryId,
        level: path.length,
        name,
        pathLabel: categoryPath.join(' › '),
      });
    }

    (childrenByParentId.get(categoryId) || [])
      .sort(sortByPositionAndName)
      .forEach(child => visit(child, categoryPath));
  };

  (childrenByParentId.get('root') || [])
    .sort(sortByPositionAndName)
    .forEach(category => visit(category, []));

  categoryList
    .filter(category => !visited.has(normalizedId(category?.id)))
    .sort(sortByPositionAndName)
    .forEach(category => visit(category, []));

  return destinations;
};

const MoveDetailRowModal = ({
  categories,
  isLoading,
  isMoving,
  onConfirm,
  onHide,
  rows,
  show,
  sourceCategoryId,
}) => {
  const lineItems = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const isMultipleRows = lineItems.length > 1;
  const lineItemIds = lineItems.map(row => row.id).join(':');
  const [destinationId, setDestinationId] = useState('');
  const [searchText, setSearchText] = useState('');
  const destinations = useMemo(
    () => buildDetailViewDestinations(categories, sourceCategoryId),
    [categories, sourceCategoryId]
  );
  const filteredDestinations = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase();
    if (!query) return destinations;
    return destinations.filter(destination =>
      destination.pathLabel.toLocaleLowerCase().includes(query)
    );
  }, [destinations, searchText]);

  useEffect(() => {
    if (!show) return;
    setDestinationId('');
    setSearchText('');
  }, [lineItemIds, show]);

  const handleConfirm = () => {
    if (!destinationId || isMoving) return;
    onConfirm(destinationId);
  };

  return (
    <Modal
      centered
      className="move-detail-row-modal"
      onHide={onHide}
      show={show}
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title>Move Line Item{isMultipleRows ? 's' : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="move-detail-row-modal__description">
          {isMultipleRows ? (
            <>
              Move <strong>{lineItems.length} line items</strong> to the bottom
              of another detail view, preserving their current order.
            </>
          ) : (
            <>
              Move <strong>{lineItems[0]?.item || 'this line item'}</strong> to
              the bottom of another detail view.
            </>
          )}
        </p>
        <label
          className="move-detail-row-modal__label"
          htmlFor="move-detail-row-search"
        >
          Destination detail view
        </label>
        <input
          autoFocus
          className="move-detail-row-modal__search"
          id="move-detail-row-search"
          onChange={event => setSearchText(event.target.value)}
          placeholder="Search detail views"
          type="search"
          value={searchText}
        />
        <div
          aria-label="Destination detail views"
          className="move-detail-row-modal__destinations"
          role="listbox"
        >
          {isLoading ? (
            <div className="move-detail-row-modal__status">
              <Spinner animation="border" size="sm" /> Loading detail views…
            </div>
          ) : null}
          {!isLoading && !filteredDestinations.length ? (
            <div className="move-detail-row-modal__status">
              No other detail views match your search.
            </div>
          ) : null}
          {filteredDestinations.map(destination => (
            <button
              aria-selected={destinationId === destination.id}
              className={`move-detail-row-modal__destination ${
                destinationId === destination.id ? 'is-selected' : ''
              }`}
              key={destination.id}
              onClick={() => setDestinationId(destination.id)}
              role="option"
              style={{ '--destination-depth': destination.level }}
              type="button"
            >
              <span className="move-detail-row-modal__destination-name">
                {destination.name}
              </span>
              <small className="move-detail-row-modal__destination-path">
                {destination.pathLabel}
              </small>
            </button>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={isMoving} onClick={onHide} variant="secondary">
          Cancel
        </Button>
        <Button
          disabled={!destinationId || isMoving}
          onClick={handleConfirm}
          variant="primary"
        >
          {isMoving ? 'Moving…' : `Move Line Item${isMultipleRows ? 's' : ''}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

MoveDetailRowModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isMoving: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  show: PropTypes.bool,
  sourceCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MoveDetailRowModal.defaultProps = {
  categories: [],
  isLoading: false,
  isMoving: false,
  rows: [],
  show: false,
  sourceCategoryId: null,
};

export default MoveDetailRowModal;
